import React, { useEffect } from "react";
import { Button, CustomSelect } from "components";
import { ConsultantProfileData } from "./types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { GalleryUploadIcon } from "assets";

const consultantProfileSchema = yup.object({
  industryFocus: yup.object({
    label: yup.string().required("Required"),
    value: yup.string().required("Required"),
  }),
  clientProjectVolume: yup.object().shape({
    value: yup.string().required("Required"),
    label: yup.string().required("Required"),
  }),
  profile_picture: yup.string().required("Required"),
});

const industryFocusOptions = [
  { label: "Single Industry", value: "single_industry" },
  { label: "Multiple Industries", value: "multiple_industries" },
];

const projectVolumeOptions = [
  { label: "1—10", value: "1-10" },
  { label: "11—50", value: "11-50" },
  { label: "51—100", value: "51-100" },
  { label: "100+", value: "100+" },
];

interface ConsultantProfileProps {
  submit: (data: ConsultantProfileData) => void;
  goBack: () => void;
  handleImageUpload: () => void;
  image: string | null;
  imageURL: string | null;
  existingProfilePictureURL?: string | null;
  isSubmitting?: boolean;
}

export const ConsultantProfileForm: React.FC<ConsultantProfileProps> = ({
  submit,
  goBack,
  handleImageUpload,
  image,
  imageURL,
  existingProfilePictureURL,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    setValue,
  } = useForm<ConsultantProfileData>({
    resolver: yupResolver(consultantProfileSchema),
    mode: "onTouched",
    defaultValues: {
      industryFocus: { label: "", value: "" },
      clientProjectVolume: { label: "", value: "" },
      profile_picture: "",
    },
  });

  const hasNewPicture = !!imageURL;
  const displayPictureURL = imageURL || existingProfilePictureURL;

  useEffect(() => {
    if (image) {
      setValue("profile_picture", image, { shouldValidate: true });
    }
  }, [image, setValue]);

  return (
    <form className="max-h-[380px] overflow-y-auto scrollbar-none">
      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Profile Picture <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center ${(isSubmitted && !hasNewPicture) || errors.profile_picture ? 'border-2 border-red-500' : ''}`}>
            {displayPictureURL ? (
              <img
                src={displayPictureURL}
                alt="Profile Picture"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>
          <button
            type="button"
            onClick={handleImageUpload}
            className="px-4 py-2 !ml-0 border border-[#012169] text-[#012169] rounded-md text-sm font-medium bg-white hover:bg-blue-50 transition-colors flex items-center gap-2"
          >
            <GalleryUploadIcon className="w-4 h-4" />
            Upload profile picture
          </button>
        </div>
        {(isSubmitted && !hasNewPicture) || errors.profile_picture?.message ? (
          <p className="text-xs text-red-500 block mt-2">
            {errors.profile_picture?.message?.toString() || "Profile picture is required"}
          </p>
        ) : null}
      </div>

      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Industry focus
        </label>
        <CustomSelect
          placeholder="Single Industry"
          options={industryFocusOptions}
          onChange={(val) => setValue("industryFocus", val)}
          validatorMessage={errors.industryFocus?.value?.message?.toString() ?? ""}
          name="industryFocus"
          value={watch("industryFocus")}
          showRequired
        />
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Approx. number of client projects per year
        </label>
        <CustomSelect
          placeholder="1—10"
          options={projectVolumeOptions}
          onChange={(val) => setValue("clientProjectVolume", val)}
          validatorMessage={errors.clientProjectVolume?.value?.message?.toString() ?? ""}
          name="clientProjectVolume"
          value={watch("clientProjectVolume")}
          showRequired
        />
      </div>

      <div className="flex gap-4">
        <Button
          className="max-w-sm flex-1 border !border-[#012169] !text-[#012169] bg-white"
          type="outline"
          onClick={goBack}
        >
          Back
        </Button>
        <Button
          className="max-w-sm flex-1 !bg-[#012169] !border-none text-white"
          type="fill"
          onClick={handleSubmit(submit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Completing Setup..." : "Complete Setup"}
        </Button>
      </div>
    </form>
  );
};